import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { Modal, Select, Input, Button, Row, Col, message } from 'antd';
import { useBlockListConfig } from 'hooks/business/useBlockListConfig';
import React, { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { addBlockBuyer } from 'services/api/blockList';
import { getTalkInfo } from 'services/api/conversation.convList';
import { buyerNickMask } from 'services/channels/demo';
import { ReactComponent as iconAdd } from '../assets/addToBlockList.svg';
import useReasons from '../hooks/useReasons';
import { useSetConversation, useGetConversationById } from 'hooks/business/lruConversation';

interface Props {
  buyerId: string;
  buyerNick: string;
  storeId: string;
  conversationId: string;
  children?: React.ReactNode;
  channel: General.PlatformUpperCaseTunnel;
}

const Container = styled.div`
  .scope-select {
    background-color: #f0f2f5;
    padding: 4px;
    display: inline-block;
    .ant-btn {
      color: #336fff;
    }
    .ant-btn-text {
      color: #4e5969;
    }
  }
  .has-error {
    .ant-select-selector {
      border-color: var(--ant-error-color);
    }
  }
`;

const AddToBlockList = React.memo((props: Props) => {
  const { buyerId, buyerNick, storeId, conversationId, children, channel } = props;
  const setConversation = useSetConversation();
  const getConversationById = useGetConversationById();

  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);

  const config = useBlockListConfig(visible);
  const [error, setError] = useState(false);
  const [value, setValue] = useState({
    blacklistScope: config?.blacklistScope || 1,
    reason: '',
    remark: '',
  });
  const reasons = useReasons(visible);
  useEffect(() => {
    const firstReason = reasons[0];
    if (firstReason) {
      setValue((pre) => ({ ...pre, reason: firstReason }));
    }
  }, [reasons]);
  return (
    <>
      <Modal
        title={t('ViewBlockList.拉黑')}
        visible={visible}
        okText={t('ViewBlockList.确认拉黑')}
        onCancel={() => setVisible(false)}
        onOk={() => {
          if (!value.reason) {
            setError(true);
            return;
          }
          // 后端不原因在 add接口返回 blacklistId，这里只能在add response 以后去请求一下 talk拿到 blacklistId
          addBlockBuyer({
            buyerId,
            storeId,
            ...value,
          })
            .then(() => getTalkInfo({ conversationId }))
            .then((res) => {
              message.success(t('ViewBlockList.拉黑成功，该买家消息将自动屏蔽'));
              const conversation = getConversationById(res.conversationId);
              setConversation(res.conversationId, {
                ...res,
                topBarInfo: conversation?.topBarInfo,
              });
              setVisible(false);
              setValue({
                blacklistScope: config?.blacklistScope || 1,
                reason: '',
                remark: '',
              });
            })
            .catch(() => {
              message.error(t('ViewBlockList.拉黑错误，请稍后重试'));
            });
        }}
      >
        <Container>
          <Row gutter={[4, 24]} align="middle">
            <Col span={4}>{t('ViewBlockList.买家昵称')}:</Col>
            <Col span={20}>{buyerNickMask(buyerNick)}</Col>
            <Col span={4}>{t('ViewBlockList.拉黑范围')}:</Col>
            <Col span={20}>
              <div className="scope-select">
                <Button
                  type={value.blacklistScope !== 2 ? 'default' : 'text'}
                  onClick={() => {
                    setValue((pre) => ({ ...pre, blacklistScope: 1 }));
                  }}
                >
                  {channel === 'WA_BIZ' ? t('WhatsApp.全部帐号') : t('ViewBlockList.全部店铺')}
                </Button>
                <Button
                  type={value.blacklistScope === 2 ? 'default' : 'text'}
                  onClick={() => {
                    setValue((pre) => ({ ...pre, blacklistScope: 2 }));
                  }}
                >
                  {channel === 'WA_BIZ' ? t('WhatsApp.仅当前帐号') : t('ViewBlockList.仅当前店铺')}
                </Button>
              </div>
            </Col>
            <Col span={4}>{t('ViewBlockList.拉黑原因')}:</Col>
            <Col span={20} className={error ? 'has-error' : ''}>
              <Select
                style={{ width: '100%' }}
                value={value.reason}
                placeholder={t('ViewBlockList.选择拉黑原因')}
                onChange={(value) => {
                  if (error) {
                    setError(false);
                  }
                  setValue((pre) => ({ ...pre, reason: value }));
                }}
              >
                {reasons.map((x) => (
                  <Select.Option value={x} key={x}>
                    {x}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={4} />
            <Col span={20}>
              <Input
                maxLength={100}
                placeholder={t('ViewBlockList.备注选填')}
                value={value.remark}
                suffix={`${value.remark.length}/100`}
                onChange={(e) => setValue((pre) => ({ ...pre, remark: e.target.value }))}
              />
            </Col>
          </Row>
        </Container>
      </Modal>
      <div onClick={() => setVisible(true)}>
        {children || (
          <>
            <Icon component={iconAdd} /> {t('ViewBlockList.拉黑')}
          </>
        )}
      </div>
    </>
  );
});

export default AddToBlockList;
